import {readNativeOutputFormat,type NativeOutputFormat} from './output-format';
import type {NativeWorkScope} from './native-envelope';

/** Company export contract: '30' means passthrough; fractional FPS is final-file conversion only. */
export type NativeOutputFrameRate = '30'|'30000/1001';
export function readNativeOutputFrameRate(value:unknown):NativeOutputFrameRate|undefined{
  if(value===undefined)return undefined;
  if(value==='30'||value==='30000/1001')return value;
  throw new Error('출력 프레임 설정을 확인해 주세요.');
}

/** Requests identify a saved work, never accept an arbitrary editable graph or storage path. */
export type NativeRenderRequest = {
  requestId: string;
  scope: NativeWorkScope;
  expectedRevision: string;
  outputDimensions?: {width:number;height:number};
  outputFrameRate?: NativeOutputFrameRate;
  outputFormat?: NativeOutputFormat;
};
export type NativeRenderJob = {
  id: string;
  scope: NativeWorkScope;
  revision: string;
  precisionSavedAt?: string;
  phase: 'rendering'|'completed'|'failed';
  progress: number;
  artifactId?: string;
  outputFormat?: NativeOutputFormat;
  message?: string;
};

export function readNativeRenderRequest(value: unknown): NativeRenderRequest {
  const record = (v:unknown):v is Record<string,unknown> => !!v && typeof v==='object' && !Array.isArray(v);
  const id = (v:unknown):v is string => typeof v==='string' && /^[a-zA-Z0-9_-]{8,128}$/.test(v);
  if(!record(value)||!id(value.requestId)||!record(value.scope)||!id(value.scope.projectId)||!id(value.scope.workId)
    || !['agent','editor'].includes(String(value.scope.surface)) || typeof value.expectedRevision!=='string'
    || !Number.isFinite(Date.parse(value.expectedRevision)) || new Date(value.expectedRevision).toISOString()!==value.expectedRevision
    || Object.keys(value).some(key=>!['requestId','scope','expectedRevision','outputDimensions','outputFrameRate','outputFormat'].includes(key)))
    throw new Error('출력할 저장본 정보를 확인해 주세요.');
  const scope=value.scope;
  if(!id(scope.projectId)||!id(scope.workId))throw new Error('작업 번호를 확인해 주세요.');
  if(scope.surface!=='agent'&&scope.surface!=='editor')throw new Error('작업 종류를 확인해 주세요.');
  const result:NativeRenderRequest={requestId:value.requestId,scope:{projectId:scope.projectId,workId:scope.workId,surface:scope.surface},expectedRevision:value.expectedRevision};
  const outputFormat=readNativeOutputFormat(value.outputFormat);
  // Omit the default to retain hashes of existing MP4 requests.
  if(outputFormat==='webm')result.outputFormat=outputFormat;
  const outputFrameRate=readNativeOutputFrameRate(value.outputFrameRate);
  if(outputFrameRate!==undefined)result.outputFrameRate=outputFrameRate;
  if(value.outputDimensions!==undefined){
    const dims=value.outputDimensions;
    if(!record(dims)||typeof dims.width!=='number'||typeof dims.height!=='number'
      ||![dims.width,dims.height].every(n=>Number.isSafeInteger(n)&&n>=2&&n<=8192&&n%2===0)
      ||Object.keys(dims).some(key=>key!=='width'&&key!=='height'))throw new Error('출력 크기는 2~8192 사이의 짝수로 지정해 주세요.');
    result.outputDimensions={width:dims.width,height:dims.height};
  }
  return result;
}
